import humps from 'humps'
// import inflect from 'i';
import { axios } from '../request'
import { toRaw } from 'vue'
import { cancelScheduledValidatesFor, scheduleValidate } from '../lib/validateQueue'
import { ARRAY_SHAPE, OBJECT_SHAPE, readEnvelope } from '../lib/apiEnvelope'

// const inflect = require('i')(true);

function processParams(params) {
  const p = JSON.parse(JSON.stringify(params))
  if (typeof params.sort.name === 'undefined') {
    p.sortName = 'created_at'
  } else {
    p.sortName = params.sort.name
  }
  if (typeof params.sort.direction === 'undefined') {
    p.sortDir = ''
  } else if (params.sort.direction === 'ascending') {
    p.sortDir = ''
  } else {
    p.sortDir = '-'
  }
  if (typeof params.page === 'undefined') {
    p.page = -1
  }
  let constrains = ''
  if (typeof params.constrains !== 'undefined' && p.constrains !== '') {
    Object.keys(params.constrains).forEach((key) => {
      constrains += `&_${key}=${params.constrains[key]}`
    })
  }
  p.constrains = constrains
  return p
}

function preRequestTransform(item, state) {
  //so we can change values before submiting without changing frontend values. Useful for typecasting
  const newItem = structuredClone(toRaw(item))
  const structure = state.structure
  Object.keys(newItem).forEach((key) => {
    if (typeof structure[key] !== 'undefined') {
      if (structure[key].type === 'float32') {
        newItem[key] = parseFloat(newItem[key])
      } else if (structure[key].type === 'integer') {
        newItem[key] = parseInt(newItem[key], 10)
      }
    }
  })
  return newItem
}

function storedListSize(name, listName) {
  let listSize = 100
  if (localStorage.getItem(`${name}-${listName}ListSize`)) {
    localStorage.removeItem(`${name}-${listName}ListSize`)
  }
  /*const newSize = localStorage.getItem(`${name}ListSize`)
  if (newSize !== null) {
    listSize = parseInt(newSize, 10)
  }*/
  return listSize
}

function getListSkeleton() {
  return {
    listFields: {},
    params: {
      page: 1,
      search: '',
      sort: {},
      constrains: {},
    },
    listSize: storedListSize(name),
    loading: false,
    loaded: false,
    total: 0,
    triedSaving: false,
    data: [{}],
  }
}

export default function generateStore(name) {
  const url = humps.decamelize(name)
  // In-flight list requests, keyed by list name (#351).
  //
  // Keyed, because a store serves several named lists at once — aborting the
  // 'counter' list's request because the 'default' list started one cancels a
  // request nobody asked to cancel.
  //
  // Sequenced as well as aborted, because `abort()` is a request to stop, not a
  // guarantee that no handler runs: a response already in the microtask queue
  // still resolves. Only the newest request for a list may commit its result.
  const listRequests = {}

  // How many list loads each list has started, ever. Monotonic, unlike the `seq`
  // above, which restarts once a request completes and is deleted. See
  // `markListReplaced` below for what it is for.
  const listsStarted = {}

  /**
   * Claim a list for a new request, cancelling whatever it was waiting for.
   * Returns the signal to send and the sequence number to check on the way back.
   */
  function beginListRequest(listName) {
    const previous = listRequests[listName]
    if (previous && !previous.controller.signal.aborted) {
      previous.controller.abort()
    }
    const controller = new AbortController()
    const seq = (previous?.seq ?? 0) + 1
    listRequests[listName] = { controller, seq }
    listsStarted[listName] = (listsStarted[listName] ?? 0) + 1
    return { signal: controller.signal, seq, started: listsStarted[listName] }
  }

  /** False once a later request for the same list has superseded this one. */
  function isCurrentListRequest(listName, seq) {
    return listRequests[listName]?.seq === seq
  }

  /**
   * Give up on whatever this list was waiting for, without asking for anything
   * new (#352).
   *
   * `clearList` used to only `abort()`, and abort cannot reach a response that
   * has already been received — its `.then` still passed `isCurrentListRequest`
   * and committed over the list that had just been emptied. Every detail view
   * opens with `clearList()` then `read(id)` into row 0 of the same named list,
   * so the answer to a list the user left seconds ago would arrive and replace
   * the record on screen with the list's first row.
   *
   * Bumping the sequence is what actually closes it: the in-flight answer is no
   * longer the current request, so it returns without committing whether it was
   * aborted in time or not. A single-record `read` on the wire had the same
   * hole and `clearList` closes it the same way — see `cancelSingleReads` (#534).
   *
   * Returns whether a request was actually in flight. The aborted controller
   * stays in the entry, so cancelling twice does not report a request twice.
   */
  function cancelListRequest(listName) {
    const pending = listRequests[listName]
    if (!pending) {
      return false
    }
    const wasInFlight = !pending.controller.signal.aborted
    if (wasInFlight) {
      pending.controller.abort()
    }
    listRequests[listName] = { controller: pending.controller, seq: pending.seq + 1 }
    return wasInFlight
  }

  // Which list load a single-record write is allowed to lose to (#352).
  //
  // `read`, `create` and `update` all write their record into
  // `list[listName].data[key]` — index 0 for a single record. That is right when
  // the array is a form, and wrong when it is a *list*: several components read
  // one record into the shared `default` list purely to show a name in a header
  // (`TransactionList` does it for the cost centre whose transactions are on
  // screen), and nothing stops that write from arriving after the user has
  // navigated to the list of the same collection.
  //
  // When it does, the record lands on top of whatever was in row 0 and the list
  // shows it twice: once at row 0 and once at its own sorted position. Measured
  // on the cost-centre list: the API returned thirteen rows with exactly one
  // "Nitzsche, Fadel and Crona" in them, and the table drew that name twice.
  // Both copies then carry the same `_id`, so `v-for`'s keys are no longer
  // unique and Vue re-creates the rows on every patch — which is the other face
  // of the same failure, a row that is detached and rebuilt faster than a click
  // can land on it.
  //
  // The test cannot be "has this list been replaced since I asked?", because
  // opening a record *from* its own list is the ordinary case and produces the
  // same answer: `/developers` renders `apiSecrets` through `ListC` and
  // `ApiSecretsSingle` reads the clicked record into that same `default` list,
  // so the list request the page already had in flight lands after the read was
  // dispatched. Suppressing there leaves the detail page blank — it emptied
  // `#apiSecrets-default-0-description-value` and failed `basic.spec.js`.
  //
  // What separates the two is whether the list load *started* after the write
  // was dispatched. A read that was already waiting when the list was asked for
  // is stale; a read issued after the list was asked for is the newer intent and
  // wins. So each list counts the loads it has started, a single-record write
  // remembers that count, and a load only overrules it if its own ordinal is
  // higher.
  //
  // Only a *replacement* counts. An `append` (the infinite scroll asking for the
  // next page) leaves every existing index pointing at the record it pointed at
  // before, so row `key` still belongs to whoever was writing to it — counting it
  // here would make an in-flight `create` drop the `_id` the server just returned
  // and the next save would POST the record a second time.
  //
  // None of this cancels anything: the caller still gets its record, it just may
  // not put it into a list that has moved on.
  //
  // `clearList` replaces the list too, with nothing (#617). It is neither a
  // listing nor a read, so it used to leave this count where it was, and a
  // `create`, `update` or `modify` still on the wire wrote the record of the
  // form just left into the row of the one just emptied — a «new» form holding
  // a saved record, which its next save PUTs over itself. Counting the clear as
  // a load that started and replaced the list at once puts every write
  // dispatched before it on the losing side, and every write after it on the
  // winning one.
  const listsReplacedBy = {}

  function markListReplaced(listName, started) {
    listsReplacedBy[listName] = Math.max(listsReplacedBy[listName] ?? 0, started)
  }

  /** `clearList`'s turn as a list load: begun and replaced in one step (#617). */
  function markListCleared(listName) {
    listsStarted[listName] = (listsStarted[listName] ?? 0) + 1
    markListReplaced(listName, listsStarted[listName])
  }

  /** The ordinal a write dispatched now must beat to keep its row. */
  function listStartedCount(listName) {
    return listsStarted[listName] ?? 0
  }

  /** True once a list load *begun after* `startedAtDispatch` has replaced the list. */
  function listMovedOnSince(listName, startedAtDispatch) {
    return (listsReplacedBy[listName] ?? 0) > startedAtDispatch
  }

  // Reads competing for the same row (#352).
  //
  // #351 gave lists this rule — only the newest request for a list may commit —
  // and single-record reads need it for the same reason, because they share the
  // row. Several screens read a record into a list purely to display a name, so
  // more than one read is genuinely in flight at once:
  // `MemberApplications/Single` reads up to three *other* members into
  // `members/default` to name the ones repeating the applicant's cartão de
  // cidadão, NIF and email; `Users/Single`, `People/Single` and
  // `TransactionList` all do something similar.
  //
  // Accept the application, open the new member, and a duplicate-check read
  // still on the wire lands on top of them. Measured: the member at
  // `/socios/850f5c14-…` is `Raina Medhurst`, NIF 230821057 in the database —
  // what was asked for — while the page rendered the seeded `Olin Barton`, NIF
  // 9928626386596408745.
  //
  // Appending reads are exempt: they write at the end to build a list of
  // records on purpose, so they are not competing for a row.
  const singleReads = {}
  // The reads of each list still waiting for an answer, so `clearList` can tell
  // whether it left one behind (#534).
  const singleReadsInFlight = {}

  function beginSingleRead(listName) {
    singleReads[listName] = (singleReads[listName] ?? 0) + 1
    singleReadsInFlight[listName] ??= new Set()
    singleReadsInFlight[listName].add(singleReads[listName])
    return singleReads[listName]
  }

  function isCurrentSingleRead(listName, seq) {
    return singleReads[listName] === seq
  }

  function endSingleRead(listName, seq) {
    singleReadsInFlight[listName]?.delete(seq)
  }

  /**
   * Take row 0 away from every read of this list still on the wire (#534).
   *
   * `clearList` was neither a newer read nor a newer listing, so a read in
   * flight could not tell a cleared list from one where nothing had happened,
   * and wrote its record back into the row just emptied — #529's «Outros»
   * coming back after the field was cleared. Advancing the sequence makes
   * every older read superseded; like `cancelListRequest`, it still resolves
   * for whoever asked, it just no longer commits.
   *
   * Returns whether a read was actually in flight, because the read that is
   * now superseded will not commit `loaded` either and the caller has to clear
   * the `loading` it set.
   */
  function cancelSingleReads(listName) {
    if (singleReads[listName] === undefined) {
      return false
    }
    singleReads[listName] += 1
    const wasInFlight = (singleReadsInFlight[listName]?.size ?? 0) > 0
    singleReadsInFlight[listName]?.clear()
    return wasInFlight
  }

  // Deletes still on the wire, so a save can wait for them (#352).
  //
  // `delete` only splices the row out of the list once the request answers, and
  // the row components that dispatch it drop the promise they get back
  // (`MemberStates/SingleC.deleteLocal` is the measured case). So between the
  // click and the response the list still holds a record that no longer exists
  // on the server — and `saveAll` saves *every* row it finds there, so it PUTs
  // that one too. The API answers 404 for a row that is gone
  // (`API.CheckEmpty`), the child save rejects, and a form that saves its
  // children through `saveAllVia` is left with a rejected promise it does not
  // report: `Members/Single.saveLocal` never leaves edit mode and never
  // navigates, so the save silently did not happen.
  //
  // From the trace of the reproduction: DELETE 204 on
  // `/member_states/fc1ca727-…` at 14:03:31.977, then PUT **404** on the same id
  // at 14:03:32.066 — 88 ms later. On an idle machine the response is normally
  // applied before a save reaches the child collections, which is why this read
  // as flakiness rather than as a bug for as long as it existed.
  //
  // The rule is the one `MemberApplications/Single` already applies by hand
  // before it calls `saveAllVia`: finish the deletes, *then* save what is left.
  // Waiting is enough on its own — no row needs to be skipped, because a
  // successful delete has removed it from the list by the time the wait is over,
  // and a failed one is still there and still wants saving.
  const deletesInFlight = new Set()

  function trackDelete(promise) {
    deletesInFlight.add(promise)
    const forget = () => deletesInFlight.delete(promise)
    promise.then(forget, forget)
    return promise
  }

  /**
   * Settle every delete this collection has in flight.
   *
   * `allSettled`, not `all`: a delete that failed leaves its row in the list, so
   * the save that follows treats it as a row to save — which is what it was
   * before this wait existed. Rejecting the save because an unrelated delete
   * failed would be a new failure mode, and this is not the place to invent one.
   */
  function settlePendingDeletes() {
    if (deletesInFlight.size === 0) return Promise.resolve()
    return Promise.allSettled([...deletesInFlight])
  }

  return {
    namespaced: true,
    state: {
      cache: {
        data: {},
        pages: {},
      },
      listById: { default: [] },
      listUnpaginatedById: { default: [] },
      list: {
        default: {
          listFields: {},
          params: {
            page: 1,
            search: '',
            sort: {},
            constrains: {},
          },
          listSize: storedListSize(name, 'default'),
          loading: false,
          loaded: false,
          total: 0,
          triedSaving: false,
          data: [{}],
        },
      },
      listUnpaginated: {
        default: {
          loading: false,
          loaded: false,
          data: [],
        },
      },
      errors: {
        default: [],
      },
      structure: {},
      status: null,
    },
    mutations: {
      clearList(state, listName) {
        if (typeof state.list[listName] === 'undefined') {
          state.list[listName] = getListSkeleton()
        } else {
          state.list[listName].data = [{}]
        }
        state.listById[listName] = {}
      },
      createList(state, listName) {
        if (typeof state.list[listName] === 'undefined') {
          state.list[listName] = getListSkeleton()
        }
        if (typeof state.listById[listName] === 'undefined') {
          state.listById[listName] = {}
        }

        state.errors[listName] = []
      },
      addEmpty(state, listName) {
        state.list[listName].data.push({})
        if (typeof state.listById[listName] === 'undefined') {
          state.listById[listName] = {}
        }
      },
      setLastStatus(state, s) {
        state.status = s
      },
      setSingle(state, { key, data, listName }) {
        // #419 — the last line of defence, for the payloads this store does not
        // read itself. `res.data.data` is committed straight into `setSingle`
        // from six sibling stores (`oidcClients`, `treasuryDocs`,
        // `memberPhotos`, `pdfDocuments`, `memberApplicationPhotos`,
        // `personPhotos`), and `data._id` on a 2xx that carried no record
        // throws *inside the commit* — which is worse than a bad render,
        // because Vuex is left mid-mutation with the row already assigned.
        // Refusing the write keeps the row in the type it was born with.
        if (!OBJECT_SHAPE.matches(data)) {
          console.error(`${name}: refused a setSingle payload that is not a record`, data)
          return
        }
        state.list[listName].data[key] = data
        if (typeof state.listById[listName] === 'undefined') {
          state.listById[listName] = {}
        }
        state.listById[listName][data._id] = data
        data._k = key
      },
      saveCache(state, { fullURL, data }) {
        // The whole envelope, not the payload — `data.data` is the rows. This
        // one throws *after* emptying the page below, so a malformed answer used
        // to leave an EMPTY cache page behind. The cache branch of `list` paints
        // from that page before its own request answers, so every later repeat
        // of the same query blinked an empty list on the way in, until a good
        // answer re-cached it (#419).
        if (!ARRAY_SHAPE.matches(data?.data)) {
          console.error(`${name}: refused to cache ${fullURL} without a usable envelope`, data)
          return
        }
        const ids = []
        state.cache.pages[fullURL] = []
        data.data.forEach((item) => {
          state.cache.data[item['_id']] = item
          ids.push(item['_id'])
        })
        state.cache.pages[fullURL] = ids
      },
      clearCachePage(state, fullURL) {
        if (typeof state.cache.pages[fullURL] !== 'undefined') {
          delete state.cache.pages[fullURL]
        }
      },
      appendToList(state, { data, listName }) {
        // #419 — `data.filter` below is the throw. Same defence as `setSingle`:
        // refuse the write rather than take the list out of its type.
        if (!ARRAY_SHAPE.matches(data)) {
          console.error(`${name}: refused an appendToList payload that is not a list`, data)
          return
        }
        if (typeof state.list[listName].data[0] !== 'undefined') {
          if (Object.keys(state.list[listName].data[0]) <= 1) {
            state.list[listName].data = []
          }
        }

        // Never append a row that is already on screen (#351).
        //
        // Offset pagination is not stable. `?_page=2` means "rows 101-200 of the
        // query as it is right now", so a row inserted or deleted between the two
        // fetches shifts the boundary and the next page begins with a row the
        // previous one ended with. The infinite scroll appends whatever comes
        // back, and nothing upstream promises the pages are disjoint.
        //
        // A duplicated row is not merely untidy. It was reaching the screen with
        // one record shown twice — verified by querying all sixteen fleet
        // databases at the moment of a failure and finding exactly one matching
        // row — and any locator that addresses a row by its content is ambiguous
        // the moment that happens.
        //
        // Deduplicating here rather than at the call site is deliberate: this is
        // the only place rows are added to an existing list, so the guarantee
        // holds however the overlap was produced.
        const alreadyShown = new Set(
          state.list[listName].data
            .map((item) => item._id)
            .filter((id) => typeof id !== 'undefined')
        )
        const fresh = data.filter(
          (item) => typeof item._id === 'undefined' || !alreadyShown.has(item._id)
        )

        let lastKey = state.list[listName].data.length - 1
        fresh.forEach((item, index) => {
          lastKey += 1
          fresh[index]._k = lastKey
        })
        state.list[listName].data.push(...fresh)
        fresh.forEach((item) => {
          state.listById[listName][item._id] = item
        })
      },
      setList(state, { data, listName }) {
        // #419 — the guard has to sit here, before the assignment, not around
        // the `forEach`. Guarding only the iteration would leave the bad value
        // in `state.list[listName].data` and still throw, so the `loaded` and
        // `setTotal` commits that follow in the `list` action would never run:
        // the flags and the data would disagree and the next render would read
        // a state no code path intended.
        if (!ARRAY_SHAPE.matches(data)) {
          console.error(`${name}: refused a setList payload that is not a list`, data)
          return
        }
        state.list[listName].data = data
        const listById = {}
        data.forEach((item) => {
          listById[item._id] = item
        })
        state.listById[listName] = listById
      },
      setIndexes(state, listName) {
        state.list[listName].data.forEach((item, k) => {
          item._k = k
        })
      },
      setIndexesUnpaginated(state, listName) {
        state.listUnpaginated[listName].data.forEach((item, k) => {
          item._k = k
        })
      },
      setListUnpaginated(state, { data, listName }) {
        // Same as `setList`, and reached the same way from `treasuryDocs` (#419).
        if (!ARRAY_SHAPE.matches(data)) {
          console.error(`${name}: refused a setListUnpaginated payload that is not a list`, data)
          return
        }
        state.listUnpaginated[listName].data = data
        const listById = {}
        data.forEach((item) => {
          listById[item._id] = item
        })
        state.listUnpaginatedById[listName] = listById
      },
      setListSize(state, { listSize, listName, force }) {
        let s = 0
        if (listSize === 0) {
          s = 1
        } else if (listSize > 500 && force !== true) {
          s = 500
        } else {
          s = listSize
        }

        if (listSize !== -1 && listName === 'default') {
          localStorage.setItem(`${name}-${listName}ListSize`, s)
        }
        if (listSize === -1) {
          s = -1
        }
        state.list[listName].listSize = s
      },
      setListFields(state, { fields, listName }) {
        fields.forEach((field) => {
          state.list[listName].listFields[field.name] = field.visible
        })
        const path = window.location.pathname
        sessionStorage.setItem(`${name}-ListFields-${path}-${listName}`, JSON.stringify(fields))
      },
      setTotal(state, { total, listName }) {
        state.list[listName].total = total
      },
      setErrors(state, { key, payload, listName }) {
        if (typeof state.errors[listName] === 'undefined') {
          state.errors[listName] = []
        }
        if (typeof payload !== 'undefined') {
          state.errors[listName][key] = payload
        }
      },
      clearErrors(state, listName) {
        state.errors[listName] = []
      },
      loading(state, listName) {
        state.list[listName].loaded = false
        state.list[listName].loading = true
      },
      loaded(state, listName) {
        state.list[listName].loaded = true
        state.list[listName].loading = false
      },
      notLoaded(state, listName) {
        if (typeof state.list[listName] === 'undefined') {
          state.list[listName] = {}
        }
        state.list[listName].loaded = false
        state.list[listName].loading = false
      },
      loadingUnpaginated(state, listName) {
        state.listUnpaginated[listName].loaded = false
        state.listUnpaginated[listName].loading = true
      },
      loadedUnpaginated(state, listName) {
        state.listUnpaginated[listName].loaded = true
        state.listUnpaginated[listName].loading = false
      },
      removeFromList(state, { k, listName }) {
        const id = state.list[listName].data[k]._id
        delete state.listById[listName][id]
        state.list[listName].data.splice(k, 1)
        if (state.list.length === 0) {
          state.list = [{}]
        }
      },
      setTriedSaving(state, { v, listName }) {
        state.list[listName].triedSaving = v
      },
      setFormValue(state, { key, fieldName, fieldValue, listName }) {
        let k = key
        if (typeof k === 'undefined') {
          k = 0
        }
        if (typeof state.list[listName].data[k] === 'undefined') {
          state.list[listName].data[k] = {}
        }
        state.list[listName].data[k][fieldName] = fieldValue
      },
      setParam(state, params) {
        const listName = params.listName
        delete params.listName
        Object.keys(params).forEach((paramName) => {
          state.list[listName].params[paramName] = params[paramName]
          if (paramName === 'sort') {
            const path = window.location.pathname
            sessionStorage.setItem(
              `${name}-SortOrder-${path}-${listName}`,
              JSON.stringify(params[paramName])
            )
          } else if (paramName === 'search') {
            const path = window.location.pathname
            sessionStorage.setItem(
              `${name}-Search-${path}-${listName}`,
              JSON.stringify(params[paramName])
            )
          }
        })
      },
      clearParams(state, params) {
        let listName = 'default'
        if (typeof params !== 'undefined') {
          if (typeof params.listName !== 'undefined') {
            listName = params.listName
          }
        }
        state.list[listName].params.page = 1
        state.list[listName].params.search = ''
        state.list[listName].params.sort = {}
        state.list[listName].params.constrains = {}
        const path = window.location.pathname
        sessionStorage.removeItem(`${name}-SortOrder-${path}-${listName}`)
        sessionStorage.removeItem(`${name}-Search-${path}-${listName}`)
      },
      setConstrain(state, constrains) {
        const listName = constrains.listName
        delete constrains.listName
        Object.keys(constrains).forEach((constrainName) => {
          if (typeof constrains[constrainName] === 'undefined') {
            delete state.list[listName].params.constrains[constrainName]
          } else {
            state.list[listName].params.constrains[constrainName] = constrains[constrainName]
          }
        })
      },
      clearConstrains(state, listName) {
        let lN = listName
        if (typeof listName === 'undefined') {
          lN = 'default'
        }
        state.list[lN].params.constrains = {}
      },
      setStructure(state, payload) {
        const structure = {}
        if (payload !== null) {
          payload.forEach((item) => {
            structure[item.name] = {
              type: item.type,
              validation: item.validation,
              label: item.label,
              group: item.group,
              inputType: item.inputType,
            }
          })
          state.structure = structure
        }
      },
    },
    actions: {
      addEmpty(context, listName) {
        return new Promise((resolve) => {
          let lN = listName
          if (typeof listName === 'undefined') {
            lN = 'default'
          }
          context.commit('addEmpty', lN)
          context.commit('setIndexes', lN)
          resolve(context.state.list[lN].data[context.state.list[lN].data.length - 1])
        })
      },
      clearList(context, listName) {
        return new Promise((resolve) => {
          let lN = listName
          if (typeof listName === 'undefined') {
            lN = 'default'
          }
          // The cancelled request returns without committing, so the
          // `loading` its start set is cleared here — or a view that opens
          // with clearList (TransactionSingle) spins forever. Both are
          // cancelled, so neither can short-circuit the other (#534).
          const cancelledList = cancelListRequest(lN)
          const cancelledRead = cancelSingleReads(lN)
          if (cancelledList || cancelledRead) {
            context.commit('notLoaded', lN)
          }
          // And the writes, which nothing above reaches (#617).
          markListCleared(lN)

          // Drop validates scheduled for records this clears, so a stray timer
          // cannot fire against a list that no longer holds them (#291).
          cancelScheduledValidatesFor(`${name}:${lN}:`)
          context.commit('clearList', lN)
          context.commit('setIndexes', lN)
          context.dispatch('setTriedSaving', { v: false, listName: lN })
          context.commit('clearErrors', lN)
          resolve()
        })
      },
      init(context, listName) {
        //For namedLists create list if it doesn't exist
        return new Promise((resolve) => {
          context.commit('createList', listName)
          const path = window.location.pathname
          const sortOrderStr = sessionStorage.getItem(`${name}-SortOrder-${path}-${listName}`)
          if (typeof sortOrderStr !== 'undefined') {
            if (sortOrderStr !== null) {
              const sort = JSON.parse(sortOrderStr)
              context.commit('setParam', { sort: sort, listName })
            }
          }
          const searchStr = sessionStorage.getItem(`${name}-Search-${path}-${listName}`)
          if (typeof searchStr !== 'undefined') {
            if (searchStr !== null) {
              const search = JSON.parse(searchStr)
              context.commit('setParam', { search: search, listName })
            }
          }
          const listFieldsStr = sessionStorage.getItem(`${name}-ListFields-${path}-${listName}`)
          if (typeof listFieldsStr !== 'undefined') {
            if (listFieldsStr !== null) {
              const listFields = JSON.parse(listFieldsStr)
              context.commit('setListFields', { fields: listFields, listName })
            }
          }
          resolve()
        })
      },
      saveAllVia(context, { resource, id, listName }) {
        return new Promise((resolve, reject) => {
          let lN = listName
          if (typeof listName === 'undefined') {
            lN = 'default'
          }
          const urlPrefix = `/${resource}/${id}/`
          context
            .dispatch('saveAll', { urlPrefix, listName: lN })
            .then(() => {
              resolve()
            })
            .catch((err) => {
              reject(new Error('Cannot save all via', context, err))
            })
        })
      },
      async saveAll(context, { urlPrefix, listName }) {
        let lN = listName
        if (typeof listName === 'undefined') {
          lN = 'default'
        }
        // Do not read the list while a delete is still on the wire: the row it
        // is removing is already gone on the server, and saving it PUTs an id
        // that answers 404 (#352 — see `deletesInFlight`).
        await settlePendingDeletes()
        const promises = []
        context.state.list[lN].data.forEach((v, key) => {
          promises.push(context.dispatch('save', { key, urlPrefix, listName: lN }))
        })
        return Promise.all(promises)
      },
      // `query` (optional) is appended to the request URL — e.g.
      // 'deferMissing=true' for a member saved as incomplete (#501).
      save(context, { key, urlPrefix, listName, query }) {
        return new Promise((resolve, reject) => {
          let lN = listName
          if (typeof listName === 'undefined') {
            lN = 'default'
          }
          let k = key
          if (typeof key === 'undefined') {
            k = 0
          }
          if (typeof context.state.list[lN].data[k]._created_at !== 'undefined') {
            context
              .dispatch('update', { key: k, urlPrefix, listName: lN, query })
              .then((data) => {
                //context.commit('setSingle', { key: k, data: res.data.data })
                resolve(data)
              })
              // #414 — `err.cause ?? err` rather than `err`, so the chain stays
              // one link deep and always ends at the axios error. Wrapping the
              // wrapper would bury the response one level further down on every
              // hop, and callers would have to know how many there were.
              .catch((err) =>
                reject(new Error('Update failed ' + err, { cause: err.cause ?? err }))
              )
          } else {
            context
              .dispatch('create', { key: k, urlPrefix, listName: lN, query })
              .then((data) => {
                resolve(data)
              })
              .catch((err) =>
                reject(new Error('Create failed ' + err, { cause: err.cause ?? err }))
              )
          }
        })
      },
      create(context, { key, urlPrefix, listName, query }) {
        return new Promise((resolve, reject) => {
          let lN = listName
          if (typeof listName === 'undefined') {
            lN = 'default'
          }
          if (typeof context.state.list[lN] === 'undefined') {
            context.commit('createList', lN)
          }
          context.dispatch('setTriedSaving', { v: true, listName: lN })
          let p = urlPrefix
          if (typeof urlPrefix === 'undefined') p = ''
          const startedAtDispatch = listStartedCount(lN)
          axios
            .post(
              `${p}${url}${query ? '?' + query : ''}`,
              preRequestTransform(context.state.list[lN].data[key], context.state)
            )
            .then((res) => {
              const { ok, data } = readEnvelope(res, OBJECT_SHAPE, `${name}.create POST ${p}${url}`)
              // The errors of row `key` belong to the same row as its data: once
              // the list has moved on they are another form's (#617).
              const movedOn = listMovedOnSince(lN, startedAtDispatch)
              if (!movedOn) {
                context.commit('setErrors', { key, payload: {}, listName: lN })
              }
              context.commit('setLastStatus', res.status)
              if (!ok) {
                // A 2xx carrying no record. The row keeps no `_id`, so it is not
                // saved as far as this store is concerned and the next save would
                // POST it a second time — reporting success would be a lie of the
                // exact kind #414 was about. Rejecting is also what this path
                // already did: `setSingle` threw on `data._id` and the `.catch`
                // below, chained after this `.then`, turned it into a rejection.
                reject(new Error(`Create of ${url} answered without a record`, { cause: res }))
                return
              }
              // See `update`: the new record must not be written into a row of a
              // list whose load was *started* after this create was sent, or the
              // list shows it twice (#352).
              if (!movedOn) {
                context.commit('setSingle', { key, data, listName: lN })
                context.commit('setIndexes', lN)
              }
              context.dispatch('setTriedSaving', { v: false, listName: lN })
              resolve(data)
            })
            .catch((err) => {
              if (err.response) {
                if (!listMovedOnSince(lN, startedAtDispatch)) {
                  context.commit('setErrors', {
                    key,
                    payload: err.response.data.errors,
                    listName: lN,
                  })
                }
                context.commit('setLastStatus', err.response.status)
              }
              // #414 — same as `update`: keep the response reachable. Both
              // branches of `save` must reject the same shape, or a component
              // reading the server's reason would find it on an edit and not on
              // a create.
              reject(new Error(err, { cause: err }))
            })
        })
      },
      list(context, options) {
        let append = false
        let lN = 'default'
        if (typeof options !== 'undefined') {
          if (options.append) {
            append = true
          }
          if (options.listName) {
            lN = options.listName
          }
        }

        // Cancel whatever this list was already waiting for. The guard here used
        // to read `if (signal.aborted) abort()`, which can only ever be true for
        // a request that was already cancelled — so a live one was never stopped
        // (#351). Every search box dispatches this per keystroke and the
        // infinite scroll dispatches it with `append`, so overlapping requests
        // are the normal case: without the cancel, the answer the user sees is
        // whichever one the network returns last.
        const { signal, seq, started } = beginListRequest(lN)
        return new Promise((resolve) => {
          const params = context.state.list[lN].params
          const p = processParams(params)
          p.signal = signal
          const limit = context.state.list[lN].listSize
          context.commit('loading', lN)
          const fullURL = `${url}?_page=${p.page}&_limit=${limit}&_sort=${p.sortDir}${p.sortName}&_search=${params.search}${p.constrains}`
          // loadCache
          if (typeof context.state.cache.pages[fullURL] !== 'undefined') {
            context.commit('clearErrors', lN)
            const data = []
            context.state.cache.pages[fullURL].forEach((id) => {
              data.push(context.state.cache.data[id])
            })
            if (!append) {
              context.commit('setList', { data, listName: lN })
              markListReplaced(lN, started)
            } else {
              context.commit('appendToList', { data, listName: lN })
            }
            context.commit('setIndexes', lN)
          }
          axios
            .get(fullURL, p)
            .then((res) => {
              const { ok, data } = readEnvelope(res, ARRAY_SHAPE, `${name}.list GET ${fullURL}`)
              // A newer request for this list has already been asked for, so this
              // answer is out of date whatever it contains. Committing it is how
              // a stale page replaced a filtered one — and, with `append`, how
              // the same row ended up on screen twice (#351).
              if (!isCurrentListRequest(lN, seq)) {
                resolve(data)
                return
              }
              delete listRequests[lN]
              if (!ok) {
                // Not a successful load, so nothing here may say it was (#419).
                //
                // The list is deliberately left holding whatever it held: a
                // garbled answer is no reason to destroy rows that were
                // legitimately loaded, and this path used to leave `loading`
                // true for good — a spinner over stale rows, which is the one
                // outcome that tells the user nothing.
                //
                // `clearCachePage` matters more than it looks. `saveCache`
                // empties the page before it walks the rows, so the throw used
                // to leave an empty page behind, and the cache branch above
                // paints from that page before its own request answers — so
                // every later repeat of this query blinked an empty list on the
                // way in until a good answer re-cached it.
                context.commit('clearErrors', lN)
                context.commit('clearCachePage', fullURL)
                context.commit('notLoaded', lN)
                context.commit('setLastStatus', res.status)
                // `[]`, like the non-404 error path: callers treat what they get
                // as the list (`structuredClone(res).map(…)`), and there are no
                // usable rows to hand them (#352).
                resolve([])
                return
              }
              context.commit('clearErrors', lN)
              context.commit('saveCache', { fullURL, data: res.data })
              if (!append) {
                context.commit('setList', { data, listName: lN })
                markListReplaced(lN, started)
              } else {
                context.commit('appendToList', { data, listName: lN })
              }
              context.commit('setIndexes', lN)
              // `meta` is written by every `API.ResponseList`, but `total` only
              // when the request was paginated (`_limit` other than -1), so this
              // is optional by design and must not be the thing that throws.
              context.commit('setTotal', { total: res.data.meta?.total, listName: lN })
              context.commit('loaded', lN)
              context.commit('setLastStatus', res.status)
              resolve(data)
            })
            .catch((err) => {
              // Every path out of here resolves with an array (#352). Callers
              // treat the resolved value as the list —
              // `TransactionList` does `costCenterOptions = structuredClone(res)`
              // — so resolving with `undefined` does not merely lose data, it
              // makes a computed throw `Cannot read properties of undefined
              // (reading 'map')`, and a computed that throws takes the whole
              // component's render with it. That page kept its cost-centre
              // header and lost the list, the filters and the "Adicionar
              // Receita" link, which is a 40 s timeout waiting for a link that
              // can no longer exist.

              // A cancelled request is not a failure and must not touch the
              // list: the error path below empties it and drops the cache page,
              // which would blank the results the *newer* request just put on
              // screen. Hand back what the list currently holds; the request
              // that superseded this one will deliver the real rows.
              if (signal.aborted || !isCurrentListRequest(lN, seq)) {
                // `toRaw`, because callers `structuredClone` what they get and
                // the success path hands them a plain array from axios; the
                // contract should not change with the path taken.
                resolve(toRaw(context.state.list[lN].data))
                return
              }
              context.commit('notLoaded', lN)
              delete listRequests[lN]
              context.commit('clearCachePage', fullURL)
              context.commit('clearErrors', lN)
              if (err.response) {
                context.commit('setLastStatus', err.response.status)
                context.commit('setList', { data: [{}], listName: lN })
                markListReplaced(lN, started)
                if (err.response.status === 404) {
                  // This API answers an empty list with 404, so this is the
                  // ordinary "no rows" case, not an error.
                  context.commit('loaded', lN)
                  context.commit('setTotal', { total: 0, listName: lN })
                  resolve([])
                  return
                }
              }
              // Anything else — a 500, or no response at all. This used to fall
              // off the end without resolving, leaving every caller waiting for
              // good.
              resolve([])
            })
        })
      },

      listUnpaginated(context, listName) {
        return new Promise((resolve) => {
          let lN = listName
          if (typeof listName === 'undefined') {
            lN = 'default'
          }
          // Same inverted guard as `list` had, same fix (#351). This list is the
          // read-only transport (#307), so a stale answer here shows the wrong
          // rows in exports and pickers rather than duplicating them.
          const { signal, seq } = beginListRequest(`unpaginated:${lN}`)
          const params = context.state.list[lN].params
          const p = processParams(params)
          p.signal = signal
          context.commit('loadingUnpaginated', lN)
          const fullURL = `${url}?_page=1&_limit=-1&_sort=${p.sortDir}${p.sortName}&_search=${params.search}${p.constrains}`
          axios
            .get(fullURL, p)
            .then((res) => {
              const { ok, data } = readEnvelope(
                res,
                ARRAY_SHAPE,
                `${name}.listUnpaginated GET ${fullURL}`
              )
              if (!isCurrentListRequest(`unpaginated:${lN}`, seq)) {
                resolve()
                return
              }
              context.commit('clearErrors', lN)
              context.commit('setLastStatus', res.status)
              if (!ok) {
                // Leave the list as it was and do not claim it loaded. This path
                // used to throw in the commit and fall into the `.catch` below,
                // which resolves nothing when there is no `err.response` — so
                // every caller waited for good (#419). Settling is the fix.
                resolve()
                return
              }
              context.commit('setListUnpaginated', { data, listName: lN })
              context.commit('loadedUnpaginated', lN)
              resolve()
            })
            .catch((err) => {
              if (signal.aborted || !isCurrentListRequest(`unpaginated:${lN}`, seq)) {
                resolve()
                return
              }
              context.commit('clearErrors', lN)
              if (err.response) {
                context.commit('setLastStatus', err.response.status)
                if (err.response.status === 404) {
                  context.commit('loaded', lN)
                  resolve()
                } else {
                  context.commit('notLoaded', lN)
                }
              } else {
                context.commit('notLoaded', lN)
              }
            })
        })
      },
      clearUnpaginated(context, listName) {
        let lN = listName
        if (typeof listName === 'undefined') {
          lN = 'default'
        }
        context.commit('setListUnpaginated', { data: [{}], listName: lN })
      },
      listFrom(context, { resource, id, append, listName }) {
        return new Promise((resolve) => {
          let lN = listName
          if (typeof listName === 'undefined') {
            lN = 'default'
          }
          context.commit('loading', lN)
          axios
            .get(`/${resource}/${id}/${url}`)
            .then((res) => {
              const { ok, data } = readEnvelope(
                res,
                ARRAY_SHAPE,
                `${name}.listFrom GET /${resource}/${id}/${url}`
              )
              if (!ok) {
                // What this action's own error path does — empty the sub-list
                // rather than leave the previous parent's rows under a new
                // parent — plus a settled promise carrying an array instead of
                // `undefined` (#419). All three callers ignore the value; the
                // type is what stops a future one from throwing on `.map`.
                context.commit('setList', { data: [], listName: lN })
                context.commit('notLoaded', lN)
                resolve([])
                return
              }
              if (append) {
                context.commit('appendToList', { data, listName: lN })
              } else {
                context.commit('setList', { data, listName: lN })
              }
              context.commit('setIndexes', lN)
              context.commit('setTotal', { total: res.data.meta?.total, listName: lN })
              context.commit('loaded', lN)
              resolve(data)
            })
            .catch((err) => {
              context.commit('setList', { data: [], listName: lN })
              if (err.response) {
                if (err.response.status === 404) {
                  context.commit('loaded', lN)
                  resolve()
                }
              } else {
                context.commit('notLoaded', lN)
                resolve()
              }
            })
        })
      },
      read(context, params) {
        //can have id or {id: v, append: bool}
        return new Promise((resolve, reject) => {
          let id
          let key = 0
          if (typeof params === 'object' && !Array.isArray(params) && params !== null) {
            id = params.id
          } else {
            id = params
          }
          let lN = 'default'
          if (typeof params !== 'undefined' && params !== null) {
            if (params.listName) {
              lN = params.listName
            }
          }

          if (typeof context.state.list[lN] === 'undefined') {
            context.commit('createList', lN)
          }
          context.commit('setErrors', { key, payload: {}, listName: lN })
          context.commit('notLoaded', lN)
          if (typeof id === 'undefined') {
            reject(new Error(`missing id when calling read on ${url}`))
            return
          }
          context.commit('loading', lN)
          const startedAtDispatch = listStartedCount(lN)
          // True once a list load *begun after* this read has replaced the list,
          // in which case row `key` belongs to that list and not to this record
          // (#352). Appending is exempt: it asks to grow the list on purpose.
          const listMovedOn = () => !params?.append && listMovedOnSince(lN, startedAtDispatch)
          // Claim row `key` for this read, so an older one cannot land on top of
          // it (#352). Appending reads write at the end and compete for nothing.
          const readSeq = params?.append ? null : beginSingleRead(lN)
          const supersededByNewerRead = () => readSeq !== null && !isCurrentSingleRead(lN, readSeq)
          axios
            .get(`${url}/${id}`)
            .then((res) => {
              endSingleRead(lN, readSeq)
              const { ok, data } = readEnvelope(res, OBJECT_SHAPE, `${name}.read GET ${url}/${id}`)
              if (params.append) {
                key = context.state.list[lN].data.length
                if (context.state.list[lN].length === 1) {
                  if (Object.keys(context.state.list[lN].data[0]).length === 0) {
                    key = 0
                  }
                }
              }
              context.commit('clearErrors', lN)
              if (!ok) {
                // Resolve with NOTHING, exactly like the 404 this API answers for
                // a record that is not there (`API.CheckEmpty`) — and NOT with an
                // empty object, however much better typed that would look (#419).
                //
                // Two callers are written against `undefined` and an empty object
                // would silently defeat both: `CreateUserQuestionOverlay` falls
                // back with `fresh ?? this.member` (#365 — an object keeps `{}`
                // and prefills the user form blank), and `TransactionSingle`
                // treats `typeof res === 'undefined'` as "this treasury document
                // is gone" and clears the link. `undefined` is also the truthful
                // answer: we have no record, not an empty one.
                //
                // Blanking the row is what this path already did — the `.catch`
                // below caught the throw from `setSingle` and blanked it there —
                // and it carries the same #352 guard, so a row that has been
                // reloaded or claimed by a later read is left alone.
                if (!listMovedOn() && !supersededByNewerRead()) {
                  context.commit('setSingle', { key, data: {}, listName: lN })
                }
                context.commit('notLoaded', lN)
                context.commit('setLastStatus', res.status)
                resolve()
                return
              }
              if (listMovedOn() || supersededByNewerRead()) {
                // The caller still gets its record — several components read one
                // only to show a name — it just does not go into a row that has
                // been reloaded, or claimed by a later read, underneath it.
                context.commit('setLastStatus', res.status)
                resolve(data)
                return
              }
              context.commit('setSingle', { key, data, listName: lN })
              context.commit('setIndexes', lN)
              context.commit('loaded', lN)
              context.commit('setLastStatus', res.status)
              resolve(data)
            })
            .catch((err) => {
              endSingleRead(lN, readSeq)
              context.commit('clearErrors', lN)
              // Same reasoning as the success path: blanking row 0 of a list that
              // has been reloaded since — or that a later read now owns — would
              // delete somebody else's row (#352).
              const stale = listMovedOn() || supersededByNewerRead()
              if (!stale) {
                context.commit('setSingle', { key, data: {}, listName: lN })
              }
              // Nor are the flags its own any more (#617): a late 404 marked a
              // cleared list `loaded`, and ended the `loading` of the read that
              // superseded it while that one was still waiting. The success path
              // already leaves them to whoever owns the row now.
              if (err.response) {
                context.commit('setLastStatus', err.response.status)
                if (err.response.status === 404) {
                  if (!stale) {
                    context.commit('loaded', lN)
                  }
                  resolve()
                }
              } else {
                if (!stale) {
                  context.commit('notLoaded', lN)
                }
                resolve()
              }
              reject(new Error('Could not read item'))
            })
        })
      },
      update(context, { key, urlPrefix, listName, query }) {
        return new Promise((resolve, reject) => {
          let lN = 'default'
          if (typeof listName != 'undefined') {
            lN = listName
          }
          context.dispatch('setTriedSaving', { v: true, listName: lN })
          let p = urlPrefix
          if (typeof urlPrefix === 'undefined') {
            p = ''
          }
          const fullurl = `${p}${url}/${context.state.list[lN].data[key]._id}${query ? '?' + query : ''}`
          const startedAtDispatch = listStartedCount(lN)
          axios
            .put(fullurl, preRequestTransform(context.state.list[lN].data[key], context.state))
            .then((res) => {
              const { ok, data } = readEnvelope(res, OBJECT_SHAPE, `${name}.update PUT ${fullurl}`)
              // As `create`: row `key`'s errors are gone with its data (#617).
              const movedOn = listMovedOnSince(lN, startedAtDispatch)
              if (!movedOn) {
                context.commit('setErrors', { key, payload: {}, listName: lN })
              }
              if (!ok) {
                // Same as `create`: a 2xx with no record back is not a save this
                // store can report as done, and rejecting is what this path
                // already did by throwing into the `.catch` below. The `cause` is
                // the response rather than an axios error, so a caller reading
                // `err.cause?.response?.data?.error` (#414) simply finds nothing
                // and falls back to its generic message.
                context.commit('setLastStatus', res.status)
                reject(new Error(`Update of ${url} answered without a record`, { cause: res }))
                return
              }
              // Row `key` belongs to a list whose load was asked for after this
              // save started, so writing the record there would show it twice —
              // once here and once at its own position (#352).
              if (!movedOn) {
                context.commit('setSingle', { key, data, listName: lN })
                context.commit('setIndexes', lN)
              }
              context.commit('setLastStatus', res.status)
              context.dispatch('setTriedSaving', { v: false, listName: lN })
              resolve(data)
            })
            .catch((err) => {
              console.error(err)
              if (err.response) {
                context.commit('setLastStatus', err.response.status)
                if (!listMovedOnSince(lN, startedAtDispatch)) {
                  context.commit('setErrors', {
                    key,
                    payload: err.response.data.errors,
                    listName: lN,
                  })
                }
              }
              // #414 — carry the axios error along as the cause. Rejecting with
              // a bare message threw the response away, so a component could
              // catch the failure but never learn what the server said: the
              // 409 the settings page gets carries its reason in the body and
              // there was no way to reach it from the other side of this
              // dispatch. `setErrors` is not that way — it is keyed by field
              // name for the validation errors of a 422, and a refusal that
              // names no field has nothing to put in it.
              reject(new Error('Failed to Update', { cause: err }))
            })
        })
      },

      modify(context, { key, fields, urlPrefix, listName }) {
        return new Promise((resolve, reject) => {
          let lN = 'default'
          if (typeof listName !== 'undefined') {
            lN = listName
          }

          context.dispatch('setTriedSaving', { v: true, listName: lN })
          // The #352 guard, which `create`, `update` and `read` have and
          // `modify` was left without: if the list is reloaded while this PATCH
          // is in flight, writing the row back would show it twice — once here
          // and once at the position the fresh list gave it (#369).
          const startedAtDispatch = listStartedCount(lN)
          let p = urlPrefix
          if (typeof urlPrefix === 'undefined') p = ''
          let k = key
          if (typeof k === 'undefined') k = 0

          let updateObject = {}
          if (typeof fields === 'undefined') {
            updateObject = preRequestTransform(context.state.list[lN].data[k], context.state)
          } else {
            // updateObject.id = context.state.list[key]._id;
            fields.forEach((field) => {
              updateObject[field] = context.state.list[lN].data[k][field]
            })
          }

          axios
            .patch(`${p}${url}/${context.state.list[lN].data[k]._id}`, updateObject)
            .then((res) => {
              const { ok, data } = readEnvelope(
                res,
                OBJECT_SHAPE,
                `${name}.modify PATCH ${p}${url}/${context.state.list[lN].data[k]._id}`
              )
              context.commit('setErrors', { key: k, payload: {}, listName: lN })
              if (!ok) {
                // As `create` and `update` (#419).
                reject(new Error(`Modify of ${url} answered without a record`, { cause: res }))
                return
              }
              if (!listMovedOnSince(lN, startedAtDispatch)) {
                context.commit('setSingle', { key: k, data, listName: lN })
                context.commit('setIndexes', lN)
              }
              context.dispatch('setTriedSaving', { v: false, listName: lN })
              resolve()
            })
            .catch((err) => {
              console.error('error', err)
              if (err.response) {
                context.commit('setErrors', {
                  key: k,
                  payload: err.response.data.errors,
                  listName: lN,
                })
              }
              // #422 — the `cause` #414 gave `update` and `save`, which `modify`
              // was left without. Every caller of `modify` is a «Guardar» on an
              // existing record (apiSecrets, oidcClients, users), and each one
              // now reads the refusal with `serverErrorMessage`; rejecting with
              // a bare message threw the response away, so those reads could
              // only ever reach their fallback sentence and the reason the
              // server gave was unreachable from the other side of the dispatch.
              reject(new Error('Failed to modify', { cause: err }))
            })
        })
      },
      setErrors(context, { key, payload, listName }) {
        if (typeof listName === 'undefined') {
          listName = 'default'
        }
        if (typeof key === 'undefined') {
          key = 0
        }
        context.commit('setErrors', { key, payload, listName })
      },

      deleteAll(context, listName) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }

        const promises = []
        function deleteNext() {
          if (context.state.list[lN].data.length > 0) {
            const k = context.state.list[lN].data.length - 1
            promises.push(
              context.dispatch('delete', { k: k, listName: lN }).then(() => {
                deleteNext()
              })
            )
          }
        }
        deleteNext()

        return Promise.all(promises)
      },

      delete(context, { k, listName }) {
        // Tracked so a save can wait for it (#352). Callers are free to drop the
        // promise — most row components do — but `saveAll` still has to know the
        // row is on its way out, or it saves a record that is already gone.
        //
        // Awaiting this promise is enough to see the list without the row:
        // `resolve()` below runs before `removeFromList`, but the commits that
        // follow it are synchronous, and any continuation of this promise is a
        // microtask, so it cannot observe the list mid-splice.
        return trackDelete(
          new Promise((resolve, reject) => {
            let lN = 'default'
            if (typeof listName !== 'undefined') {
              lN = listName
            }
            if (typeof k === 'undefined') {
              k = 0
            }
            const id = context.state.list[lN].data[k]._id
            if (typeof id !== 'undefined') {
              axios
                .delete(`${url}/${id}`)
                .then(() => {
                  // Resolve BEFORE mutating the list so parent components
                  // that gate sub-trees (e.g. DeleteC) on the record's
                  // existence don't unmount mid-emit and swallow the
                  // 'deleted' event the parent is listening for.
                  resolve()
                  context.commit('removeFromList', { k, listName: lN })
                  context.commit('setIndexes', lN)
                  context.dispatch('validate', { listName: lN })
                })
                .catch((err) => {
                  console.error(err)
                  reject(err)
                })
            } else {
              context.commit('removeFromList', { k, listName: lN })
              context.commit('setIndexes', lN)
              resolve()
              context.dispatch('validate', { listName: lN })
            }
          })
        )
      },
      validate(context, params) {
        return new Promise((resolve, reject) => {
          let lN = 'default'
          let key = undefined
          if (typeof params !== 'undefined') {
            if (typeof params.listName !== 'undefined') {
              lN = params.listName
            }
            if (typeof params.key !== 'undefined') {
              key = params.key
            }
          }
          if (typeof key !== 'undefined') {
            if (typeof context.state.list[lN].data[key] !== 'undefined') {
              axios
                .post(
                  `${url}/validate`,
                  preRequestTransform(context.state.list[lN].data[key], context.state)
                )
                .then((res) => {
                  context.commit('setErrors', { key, payload: {}, listName: lN })
                  context.commit('setLastStatus', res.status)
                  resolve()
                })
                .catch((err) => {
                  if (err.response) {
                    context.commit('setErrors', {
                      key,
                      payload: err.response.data.errors,
                      listName: lN,
                    })
                    context.commit('setLastStatus', err.response.status)
                    // An Error, like every other reject in this file (#429).
                    // This used to reject the bare `errors` object, and a
                    // rejection value that is not an Error carries no message:
                    // the one caller that forgot a `catch` reached Sentry as
                    // "Object captured as promise rejection with keys:
                    // settledDate", which names neither the store nor the
                    // request. The map stays reachable on `cause` — no caller
                    // reads it today (all four catch and ignore), the errors
                    // are already committed by `setErrors` above.
                    //
                    // `status` is what tells a caller this refusal came from a
                    // response. `cause` cannot: axios sets its own `cause` to
                    // the underlying network error, so it is populated on
                    // exactly the no-response rejections below. A caller that
                    // only wants what `request.js`'s 5xx floor cannot see
                    // (#421 — it keys on the response, and there is none)
                    // tests for `status === undefined`.
                    const refusal = new Error(`Validate of ${url} was refused`, {
                      cause: err.response.data.errors,
                    })
                    refusal.status = err.response.status
                    reject(refusal)
                    return
                  }
                  // No response: network error, timeout or abort. Callers await
                  // this before reading `errorCount` (#291), so leaving the
                  // promise unsettled would hang the submit handler for good.
                  reject(err)
                })
            } else {
              // Nothing at this key — the record was cleared after a validate
              // was scheduled for it. Vacuously valid, and it must still settle.
              resolve()
            }
          } else {
            context.commit('clearErrors', lN)
            if (context.state.list[lN].data.length === 0) {
              resolve()
              return
            }
            context.state.list[lN].data.forEach((_, key) => {
              axios
                .post(
                  `${url}/validate`,
                  preRequestTransform(context.state.list[lN].data[key], context.state)
                )
                .then((res) => {
                  context.commit('setErrors', { key, payload: {}, listName: lN })
                  context.commit('setLastStatus', res.status)
                  resolve()
                })
                .catch((err) => {
                  if (err.response) {
                    context.commit('setErrors', {
                      key,
                      payload: err.response.data.errors,
                      listName: lN,
                    })
                    context.commit('setLastStatus', err.response.status)
                  }
                })
            })
          }
        })
      },
      appendToList(context, { data, listName }) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }
        context.commit('appendToList', { data, listName: lN })
        context.commit('loaded', lN)
      },
      setTriedSaving(context, { v, listName }) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }

        context.commit('setTriedSaving', { v: v, listName: lN })
      },
      setFormObject(context, { key, data, listName }) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }
        context.commit('setFormObject', { key, data, listName: lN })
      },
      setFormValue(context, { key, fieldName, fieldValue, listName, skipValidation }) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }
        context.commit('setFormValue', { key, fieldName, fieldValue, listName: lN })
        if (typeof key !== 'undefined' && !skipValidation) {
          context.dispatch('validateDebounced', { key: key, listName: lN })
        }
      },
      // #283 — coalesce the per-keystroke validate fan-out. This is the
      // fire-and-forget path that feeds the inline field hints; anything that
      // reads `errorCount` to decide must first await `flushPendingValidates()`
      // from `lib/validateQueue` (#291).
      validateDebounced(context, { key, listName }) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }
        // Default the key exactly like `validate` does: the queue is keyed by
        // `${store}:${listName}:${key}`, so a key that drifts between scheduling
        // and flushing would leave a timer nobody can reach.
        let k = key
        if (typeof k === 'undefined') {
          k = 0
        }
        scheduleValidate(`${name}:${lN}:${k}`, () =>
          context.dispatch('validate', { key: k, listName: lN })
        )
      },
      setParam(context, payload) {
        let lN = 'default'
        if (typeof payload.listName === 'undefined') {
          payload.listName = lN
        }
        context.commit('setParam', payload)
      },
      clearParams(context, payload) {
        let lN = 'default'
        if (typeof payload !== 'undefined') {
          if (typeof payload.listName === 'undefined') {
            payload.listName = lN
          }
        }
        context.commit('clearParams', payload)
      },
      setConstrain(context, payload) {
        let lN = 'default'
        if (typeof payload.listName === 'undefined') {
          payload.listName = lN
        }
        context.commit('setConstrain', payload)
      },
      clearConstrains(context, listName) {
        let lN = 'default'
        if (typeof listName === 'undefined') {
          lN = listName
        }
        context.commit('clearConstrains', lN)
      },

      explain(context, data) {
        context.commit('setStructure', data)
      },
      setListSize(context, { size, listName, force }) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }

        context.commit('setListSize', { listSize: size, listName: lN, force })
      },
      setListFields(context, { fields, listName }) {
        let lN = 'default'
        if (typeof listName !== 'undefined') {
          lN = listName
        }
        context.commit('setListFields', { fields, listName: lN })
      },
    },
    getters: {
      errorCount(state) {
        let c = 0
        state.errors.default.forEach((item) => {
          Object.keys(item).forEach(() => {
            c += 1
          })
        })
        return c
      },
    },
  }
}
